/**
 * Sixty-six's UI module.
 *
 * What Sixty-six says that the other games do not: there is no bidding — the
 * card turned up under the talon makes trumps — and the hand is refilled from
 * that talon after every trick. So the felt carries the talon (its count, the
 * trump card under it, and whether it has been closed), and the leader's
 * three choices before leading — exchange the trump Nine, declare a marriage,
 * close — are buttons beside it, generated from `actions.turnDecisions`.
 * Going out at 66 is the server's to do the moment it can.
 */
import type { ReactNode } from 'react'
import {
  rankOf,
  sixtySixCount,
  sixtySixPoints,
  sixtySixRanking,
  suitOf,
  type CardId,
  type RankingContext,
  type Seat,
  type SixtySixContract,
  type SixtySixHandExtra,
  type Suit,
} from '@cards/shared'
import type { FeltAsideProps, GameUi, LegalCall } from '../types'
import type { TableView } from '../../table/view'
import { Glyph } from '../../table/atoms'
import { MiniCard } from '../../table/Card'
import { useSettings } from '../../settings/settings'
import { useCoach } from '../../coach/useCoach'
import { CardWord, handHint, seatStatus, sortBySuit, winningSeat } from '../common'

const MAX_TRICKS = 12
const GLYPH: Record<string, Suit> = { '♠': 'S', '♥': 'H', '♦': 'D', '♣': 'C' }
const isRecord = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null

function contractOf(view: TableView): SixtySixContract | null {
  const c = view.hand?.contract
  return isRecord(c) && typeof c.trump === 'string' ? (c as unknown as SixtySixContract) : null
}

function extraOf(view: TableView): SixtySixHandExtra | null {
  const e = view.hand?.extra
  return isRecord(e) && Array.isArray(e.marriages) ? (e as unknown as SixtySixHandExtra) : null
}

const contexts = new WeakMap<object, RankingContext>()
function rankingFor(view: TableView): RankingContext {
  const key = view.hand as object | null
  const hit = key ? contexts.get(key) : undefined
  if (hit) return hit
  const ctx = sixtySixRanking(extraOf(view)?.trump ?? contractOf(view)?.trump ?? null)
  if (key) contexts.set(key, ctx)
  return ctx
}

/** The talon: face-down cards left, and the turned card (in the kitty). */
function talonOf(view: TableView): { stock: number; turned: CardId | null } {
  const hand = view.hand
  const stock = hand?.stock?.kind === 'count' ? hand.stock.count : hand?.stock?.kind === 'cards' ? hand.stock.cards.length : 0
  const turned = hand?.kitty.kind === 'cards' ? (hand.kitty.cards[0] ?? null) : null
  return { stock, turned }
}

/** The running count, from the public record — the same function the score uses. */
function countOf(view: TableView) {
  const hand = view.hand
  const extra = extraOf(view)
  if (!hand || !extra) return null
  const playedOut = hand.holdings.every((h) => (h.kind === 'cards' ? h.cards.length : h.count) === 0)
  return sixtySixCount(hand.tricks, extra, playedOut)
}

function LabelText({ label }: { label: string }): JSX.Element {
  const parts = label.split(/([♠♥♦♣])/)
  return (
    <span>
      {parts.map((p, i) => (GLYPH[p] ? <Glyph key={i} suit={GLYPH[p]} /> : <span key={i}>{p}</span>))}
    </span>
  )
}

/** The talon on the left of the felt, and the leader's choices on the right. */
function Talon({ view, onDecide, locked }: FeltAsideProps): JSX.Element | null {
  const [settings] = useSettings()
  const coach = useCoach(view)
  const extra = extraOf(view)
  const hand = view.hand
  if (!hand || !extra) return null
  const { stock, turned } = talonOf(view)
  const closed = extra.closed
  const choices = view.isMyTurn ? (view.actions?.turnDecisions ?? []) : []
  const advice = coach?.status === 'ready' && coach.advice.action.kind === 'decide' ? JSON.stringify(coach.advice.action.decision) : null
  const empty = stock === 0 && turned === null
  return (
    <>
      <div className="talon" aria-label={empty ? 'The talon is empty' : `Talon: ${stock} face down${closed ? ', closed' : ''}`}>
        {empty ? null : (
          <div className={closed ? 'talon-pile closed' : 'talon-pile'}>
            {turned ? (
              <div className="talon-turned">
                <MiniCard card={closed ? 'BACK' : turned} deck={settings.deck} />
              </div>
            ) : null}
            {stock > 0 ? (
              <div className="talon-stock">
                <MiniCard card="BACK" deck={settings.deck} />
                <span className="talon-count">{stock}</span>
              </div>
            ) : null}
          </div>
        )}
        <div className="talon-caption">
          {empty ? 'talon out' : closed ? `closed by ${closed.seat === view.mySeat ? 'you' : view.name(closed.seat)}` : 'talon'}
        </div>
      </div>
      {choices.length > 0 ? (
        <div className="turn-choices" role="group" aria-label="Before you lead">
          {choices.map((c) => (
            <button
              key={c.key}
              type="button"
              className={`btn btn-secondary turn-choice${advice === JSON.stringify(c.decision) ? ' coach-pick' : ''}`}
              disabled={locked}
              onClick={() => onDecide(c.decision)}
            >
              <LabelText label={c.label} />
            </button>
          ))}
        </div>
      ) : null}
    </>
  )
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`
}

export const sixtySixUi: GameUi = {
  id: 'sixty-six',
  name: 'Sixty-six',
  rotation: 'anticlockwise',
  noAuction: true,
  kittyName: 'Trump card',
  options: [
    {
      key: 'target',
      label: 'Play to',
      choices: [{ label: '11 game points', value: 11 }, { label: '7', value: 7 }],
    },
  ],
  FeltAside: Talon,

  tricksPerHand: () => MAX_TRICKS,
  tricksNeeded: () => null,

  contractReading: (view) => {
    const contract = contractOf(view)
    if (!contract) return null
    const closed = extraOf(view)?.closed
    return { node: <Glyph suit={contract.trump} />, caption: closed ? 'trumps, closed' : 'trumps' }
  },

  trackCaption: (view) => {
    const count = countOf(view)
    if (!count || view.mySeat === null) return count ? `${count.total[0]} to ${count.total[1]}` : ''
    const me = view.mySeat
    const them = me === 0 ? 1 : 0
    if (count.tricks[0] + count.tricks[1] === 0) return 'first to 66'
    const pending = count.tricks[me] === 0 && count.marriages[me] > 0 ? ` (+${count.marriages[me]} once you take a trick)` : ''
    return `you ${count.total[me]}${pending}, them ${count.total[them]}`
  },

  seatStatus: (view, seat) => {
    const extra = extraOf(view)
    const parts: ReactNode[] = []
    if (extra?.closed?.seat === seat) parts.push('closed')
    const married = (extra?.marriages ?? []).filter((m) => m.seat === seat)
    if (married.length > 0) {
      parts.push(
        <span key="m">
          {married.map((m, i) => (
            <span key={m.suit}>
              {i > 0 ? ' ' : ''}
              {m.points} <Glyph suit={m.suit} />
            </span>
          ))}
        </span>,
      )
    }
    return seatStatus(view, seat, parts)
  },

  winningSeat,

  callout: (view) => {
    const trick = view.trick
    const extra = extraOf(view)
    if (!trick) return null
    if (trick.plays.length === 0 && extra) {
      // What the leader has just done before leading, in the order it happened.
      const said: ReactNode[] = []
      // "You" opens the sentence; after a semicolon it is "you".
      const who = (s: Seat) => (s === view.mySeat ? (said.length === 0 ? 'You' : 'you') : view.name(s))
      if (extra.exchange && extra.exchange.seat === trick.leader && view.hand?.kitty.kind === 'cards' && view.hand.kitty.cards[0] === extra.exchange.gave) {
        said.push(<span key="x"><b>{who(extra.exchange.seat)}</b> exchanged the 9 for <CardWord card={extra.exchange.took} /></span>)
      }
      for (const m of extra.marriages.filter((x) => x.trick === trick.index)) {
        said.push(<span key={m.suit}><b>{who(m.seat)}</b> declared {m.points} in <Glyph suit={m.suit} /></span>)
      }
      if (extra.closed && extra.closed.trick === trick.index) said.push(<span key="c"><b>{who(extra.closed.seat)}</b> closed the talon</span>)
      if (said.length > 0) return <>{said.map((s, i) => <span key={i}>{i > 0 ? '; ' : ''}{s}</span>)}</>
      return null
    }
    const leading = trick.winner ?? trick.winning
    const card = trick.plays.find((p) => p.seat === leading)?.card ?? null
    if (leading === null || card === null) return null
    const points = trick.plays.reduce((n, p) => n + sixtySixPoints(p.card), 0)
    const tail = points > 0 ? <>, {points} points</> : null
    return trick.winner !== null
      ? <><b>{view.name(leading)}</b> takes it with <CardWord card={card} />{tail}</>
      : <><b>{view.name(leading)}</b> has it with <CardWord card={card} />{tail}</>
  },

  handHint: (view) => {
    const trick = view.trick
    const extra = extraOf(view)
    const trump = extra?.trump ?? null
    if (view.isMyTurn && trick && trick.plays.length > 0 && trick.ledSuit && trump) {
      const legal = [...view.legalPlays.keys()]
      if (legal.length < view.myCards.length && legal.length > 0) {
        if (trick.ledSuit !== trump && legal.every((c) => suitOf(c) === trump)) {
          return <><b>Your turn,</b> you must trump <Glyph suit={trump} /></>
        }
        return <><b>Your turn,</b> follow <Glyph suit={trick.ledSuit} /> and beat it if you can</>
      }
      return <><b>Your turn,</b> play anything</>
    }
    if (view.isMyTurn && trick && trick.plays.length === 0) {
      const married = extra?.marriages.find((m) => m.trick === trick.index && m.seat === view.mySeat)
      if (married) return <><b>Your lead:</b> the King or Queen of <Glyph suit={married.suit} /></>
      if ((view.actions?.turnDecisions ?? []).length > 0) return <><b>Your lead,</b> or choose first</>
    }
    return handHint(view)
  },

  cardMarks: (view, card) => {
    const ctx = rankingFor(view)
    const trump = ctx.isTrump(card)
    // Half of a marriage still in hand carries what it is worth.
    const r = rankOf(card)
    const suit = suitOf(card)
    let stamp: string | null = null
    if (suit && (r === 12 || r === 13)) {
      const mate = `${r === 12 ? 13 : 12}${suit}` as CardId
      if (view.myCards.includes(mate)) stamp = suit === ctx.trumpSuit ? '40' : '20'
    }
    return { trump, stamp }
  },

  sortHand: (view, cards) => sortBySuit(cards, rankingFor(view)),

  handResult: (view) => {
    const hand = view.hand
    const result = hand?.result
    if (!hand || !result || !view.match) return null
    const extra = extraOf(view)
    const count = countOf(view)
    const winner = result.deltas.findIndex((d) => d > 0)
    if (winner === -1) {
      return { headline: 'Drawn', sentence: 'Sixty-five each. Nobody scores, and the same dealer deals again.' }
    }
    const loser = winner === 0 ? 1 : 0
    const points = result.deltas[winner]
    const mine = winner === view.mySeat
    const who = mine ? 'You' : view.name(winner)
    const closed = extra?.closed ?? null
    const pts = plural(points, 'game point')
    if (closed && closed.seat !== winner) {
      const closer = closed.seat === view.mySeat ? 'You' : view.name(closed.seat)
      return {
        headline: mine ? 'They closed short' : 'Closed short',
        sentence: `${closer} closed and did not make 66, so ${who === 'You' ? 'you take' : `${who} takes`} ${pts}${points === 3 ? ' (no trick at the close)' : ''}.`,
      }
    }
    const theirs = count ? count.total[loser] : 0
    const why = count && count.tricks[loser] === 0 ? 'without a trick' : theirs < 33 ? `with only ${theirs}` : `with ${theirs}`
    const loserName = loser === view.mySeat ? 'you' : view.name(loser)
    return {
      headline: mine ? (closed ? 'Closed and out' : 'Out') : closed ? 'They closed and went out' : 'They went out',
      sentence: `${who} reached ${count?.total[winner] ?? 66} and ${loserName === 'you' ? 'left you' : `left ${loserName}`} ${why}: ${pts}.`,
    }
  },

  // The winner of a hand deals the next; a drawn hand is dealt again by the
  // same dealer (rules §2), as `sixtySixNextDealer` decides on the server.
  nextDealer: (view) => {
    const hand = view.hand
    if (!hand?.result) return null
    const winner = hand.result.deltas.findIndex((d) => d > 0)
    return winner === -1 ? hand.dealer : winner
  },

  contractLabelNode: (label) => <LabelText label={label} />,

  // No auction: the turned card makes trumps. These keep the panel's shape
  // should one ever be drawn.
  auctionRule: 'There is no bidding: the turned card makes trumps.',
  auctionStrip: (view) => view.seats.map(() => null),
  auctionCalls: (view) => view.seats.map(() => []),
  isPass: () => false,
  explainHeadline: (call: LegalCall) => call.label,
  bidButtonLabel: (call: LegalCall) => call.label,

  decisionLayout: () => 'sheet',
  decisionHint: () => null,
  decisionWaiting: () => 'to choose',
}

